<template>
  <SolutionPageLayout title="Agencies & PCO" section="Sectors">
    <div class="space-y-14 md:space-y-16">
      <!-- Lead -->
      <header class="space-y-5">
        <p class="text-xl md:text-2xl text-primary-900 font-headings font-semibold leading-snug text-balance max-w-3xl">
          Built for professional organisers of events
        </p>
        <p>
          Entegy helps agencies and professional conference organisers deliver polished, scalable event experiences for
          clients, associations and stakeholders. Combine standout branding with highly functional event technology using one
          flexible platform for registration, communications, apps, onsite delivery and reporting.
        </p>
        <p>
          Create beautifully branded event experiences that are also deeply practical. Using a centralised platform and an
          all-in-one app tailored to each profile type through permissions and rules, Entegy helps deliver the right
          information, features and experiences to the right people at the right time — making events more engaging, connected
          and enjoyable for attendees, exhibitors, sponsors, speakers and staff.
        </p>
      </header>

      <!-- What you need -->
      <section class="rounded-2xl bg-gradient-to-br from-gray-50 to-primary-50/40 border border-gray-100/80 p-6 md:p-10 shadow-sm">
        <h2 class="text-lg md:text-xl font-headings font-bold text-primary-900 mb-6">
          What agencies and PCOs need
        </h2>
        <ul class="grid gap-4 sm:grid-cols-2">
          <li class="flex gap-3">
            <span class="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-600 text-xs font-bold text-white" aria-hidden="true">✓</span>
            <span>Flexible, intuitive tools across the complete event lifecycle</span>
          </li>
          <li class="flex gap-3">
            <span class="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-600 text-xs font-bold text-white" aria-hidden="true">✓</span>
            <span>A centralised portal for managing companies, tickets, speakers and event participation</span>
          </li>
          <li class="flex gap-3">
            <span class="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-600 text-xs font-bold text-white" aria-hidden="true">✓</span>
            <span>Custom branding, themes and domains for every event</span>
          </li>
          <li class="flex gap-3">
            <span class="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-600 text-xs font-bold text-white" aria-hidden="true">✓</span>
            <span>Beautiful apps, websites, registration forms, kiosks and communications</span>
          </li>
          <li class="flex gap-3 sm:col-span-2">
            <span class="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-600 text-xs font-bold text-white" aria-hidden="true">✓</span>
            <span>An all-in-one app with tailored functionality for exhibitors, attendees, speakers, sponsors and staff</span>
          </li>
          <li class="flex gap-3 sm:col-span-2">
            <span class="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-600 text-xs font-bold text-white" aria-hidden="true">✓</span>
            <span>Centralised reporting and analytics to support stakeholder feedback and future event planning</span>
          </li>
        </ul>
      </section>

      <!-- Audiences -->
      <section class="space-y-8">
        <h2 class="text-lg md:text-xl font-headings font-bold text-primary-900">
          Built for every audience across the event
        </h2>

        <div class="grid gap-6 lg:grid-cols-3">
          <article class="flex flex-col rounded-xl border border-gray-200 bg-white p-6 shadow-sm ring-1 ring-gray-900/5 transition-shadow hover:shadow-md">
            <h3 class="text-base font-headings font-bold text-primary-900 mb-3">
              For attendees
            </h3>
            <p class="mb-4 text-base leading-relaxed">
              Create smooth, connected attendee journeys from registration through to onsite engagement, with tailored access,
              communications and experiences for every profile type. Deliver an experience that is not only easy to navigate, but
              enjoyable to be part of.
            </p>
            <ul class="mt-auto space-y-2 border-t border-gray-100 pt-4 text-sm text-gray-700">
              <li class="pl-3 border-l-2 border-primary-400">Registration and ticketing</li>
              <li class="pl-3 border-l-2 border-primary-400">Configure profile types, permissions and rules to create tailored event journeys</li>
              <li class="pl-3 border-l-2 border-primary-400">Targeted email communications and push messaging</li>
              <li class="pl-3 border-l-2 border-primary-400">Fully featured event app</li>
              <li class="pl-3 border-l-2 border-primary-400">Gamification and passport-style competitions</li>
              <li class="pl-3 border-l-2 border-primary-400">Meetings and networking</li>
              <li class="pl-3 border-l-2 border-primary-400">Activity feed and in-event engagement tools</li>
            </ul>
          </article>

          <article class="flex flex-col rounded-xl border border-gray-200 bg-white p-6 shadow-sm ring-1 ring-gray-900/5 transition-shadow hover:shadow-md">
            <h3 class="text-base font-headings font-bold text-primary-900 mb-3">
              For exhibitors and sponsors
            </h3>
            <p class="mb-4 text-base leading-relaxed">
              Give companies the tools to manage their participation, maximise visibility and capture more value from the event
              through a branded, functional experience built specifically for their role.
            </p>
            <ul class="mt-auto space-y-2 border-t border-gray-100 pt-4 text-sm text-gray-700">
              <li class="pl-3 border-l-2 border-secondary-500">Self-service portal for companies to manage profiles, teams and event information</li>
              <li class="pl-3 border-l-2 border-secondary-500">All-in-one app for lead capture and lead qualification</li>
              <li class="pl-3 border-l-2 border-secondary-500">Tasks and information to manage participation and event readiness</li>
              <li class="pl-3 border-l-2 border-secondary-500">Sponsorship opportunities for visibility, engagement and measurable value</li>
            </ul>
          </article>

          <article class="flex flex-col rounded-xl border border-gray-200 bg-white p-6 shadow-sm ring-1 ring-gray-900/5 transition-shadow hover:shadow-md">
            <h3 class="text-base font-headings font-bold text-primary-900 mb-3">
              For staff and organisers
            </h3>
            <p class="mb-4 text-base leading-relaxed">
              Equip your delivery teams with the tools they need to manage events efficiently before and onsite, with centralised
              control over profiles, hardware, permissions, communications and access.
            </p>
            <ul class="mt-auto space-y-2 border-t border-gray-100 pt-4 text-sm text-gray-700">
              <li class="pl-3 border-l-2 border-primary-400">Powerful staff tools for onsite profile and hardware management</li>
              <li class="pl-3 border-l-2 border-primary-400">Badge printing for managed and self-service delivery</li>
              <li class="pl-3 border-l-2 border-primary-400">Centralised event controls across communications, profiles and access</li>
              <li class="pl-3 border-l-2 border-primary-400">One platform for setup, delivery and reporting</li>
            </ul>
          </article>
        </div>
      </section>

      <!-- Footer band -->
      <footer class="rounded-2xl bg-primary-900 px-6 py-8 md:px-10 md:py-10 text-primary-50 shadow-lg">
        <h2 class="text-lg md:text-xl font-headings font-bold text-white mb-3">
          Related insights
        </h2>
        <p class="text-primary-100/95 leading-relaxed max-w-3xl mb-8">
          Explore related articles, case studies and solution content to see how Entegy helps agencies and PCOs deliver
          smarter, more connected and more memorable event experiences.
        </p>
        <div class="border-t border-primary-700/80 pt-6">
          <p class="text-lg font-headings font-semibold text-white mb-2">
            Deliver more for every client event
          </p>
          <p class="text-primary-100/90 leading-relaxed max-w-3xl">
            Bring branding, registration, communications, apps, onsite tools and reporting together in one flexible platform
            built for agencies and PCOs.
          </p>
        </div>
      </footer>
    </div>
  </SolutionPageLayout>
</template>

<script setup>
import SolutionPageLayout from './SolutionPageLayout.vue'
</script>
